import React, { useState } from "react";

/** Display 5 stars; filled = gradient #08a996, empty = outline. Supports decimal e.g. 4.2 = 4 full + 1 at 20%. */
export function StarRatingDisplay({ rating }) {
  const value = rating != null ? Number(rating) : 0;
  const full = Math.floor(value);
  const partial = value - full;
  const stars = [];
  for (let i = 0; i < 5; i++) {
    let fill = 0;
    if (i < full) fill = 1;
    else if (i === full && partial > 0) fill = partial;
    stars.push(
      <span key={i} className="star_wrap" aria-hidden>
        <span className="star_bg">★</span>
        <span className="star_fill" style={{ width: `${fill * 100}%` }}>★</span>
      </span>
    );
  }
  return <span className="star_rating_display">{stars}</span>;
}

/**
 * Click stars to choose 1–5 (saved as that number). Hover previews; supports decimal `value` for display until changed.
 */
export function InteractiveStarRating({ value, onChange, disabled, busy }) {
  const [hover, setHover] = useState(null);

  const stars = [];
  for (let index = 0; index < 5; index++) {
    let displayVal;
    if (hover != null) displayVal = hover;
    else if (value != null && !Number.isNaN(Number(value))) displayVal = Number(value);
    else displayVal = 0;

    const full = Math.floor(displayVal);
    const partial = displayVal - full;
    let fill = 0;
    if (index < full) fill = 1;
    else if (index === full && partial > 0) fill = partial;

    stars.push(
      <button
        key={index}
        type="button"
        className="ns_star_pick_btn"
        disabled={disabled || busy}
        onMouseEnter={() => {
          if (!disabled && !busy) setHover(index + 1);
        }}
        onClick={() => onChange?.(index + 1)}
        aria-label={`${index + 1} star${index + 1 === 1 ? "" : "s"}`}
      >
        <span className="star_wrap ns_star_pick_wrap">
          <span className="star_bg">★</span>
          <span className="star_fill" style={{ width: `${fill * 100}%` }}>
            ★
          </span>
        </span>
      </button>
    );
  }

  return (
    <div
      className="ns_interactive_stars"
      role="group"
      aria-label="Choose 1 to 5 stars"
      onMouseLeave={() => setHover(null)}
    >
      {stars}
      {busy ? <span className="ns_interactive_stars_busy">Saving…</span> : null}
    </div>
  );
}

/** Tick icon – use with color #08a996 (Accept) */
export function TickIcon({ className = "" }) {
  return (
    <svg className={`action_icon tick_icon_svg ${className}`.trim()} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/** X (close/reject) icon – use with #08a996 or red for Reject */
export function XIcon({ className = "" }) {
  return (
    <svg className={`action_icon x_icon_svg ${className}`.trim()} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/** Trash icon – use with color #08a996 for Delete */
export function TrashIcon({ className = "" }) {
  return (
    <svg className={`action_icon trash_icon_svg ${className}`.trim()} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

/** Decimal rating options 1.0 .. 5.0 */
export const RATING_OPTIONS_DECIMAL = (() => {
  const arr = [];
  for (let r = 1; r <= 5; r += 0.5) arr.push(Math.round(r * 10) / 10);
  return arr;
})();
